import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOMetaTags from '../components/SEOMetaTags';
import CTASection from '../components/CTASection';
import { useLanguage } from '../contexts/LanguageContext';
import { whatsappLink, PHONE_NUMBER, PHONE_DISPLAY, submitLead } from '../utils/contact';

const projectMeta = {
    'pack-reforma-bano-6900': {
        isProductPack: true,
        afterImage: '/img/packs/pack-bano-1600.webp',
        beforeImage: 'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?auto=format&fit=crop&w=1600&q=80'
    },
    'reforma-integral-piso-barcelona': {
        isProductPack: false,
        afterImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80',
        beforeImage: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1600&q=80'
    },
    'reforma-cocina-salon-sabadell': {
        isProductPack: false,
        afterImage: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=1600&q=80',
        beforeImage: 'https://images.unsplash.com/photo-1507089947368-19c1da9775ae?auto=format&fit=crop&w=1600&q=80'
    },
    'reforma-bano-suite-sant-cugat': {
        isProductPack: false,
        afterImage: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1600&q=80',
        beforeImage: 'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?auto=format&fit=crop&w=1600&q=80'
    },
    'pack-reforma-cocina-12700': {
        isProductPack: true,
        afterImage: '/img/packs/pack-cuina-1600.webp',
        beforeImage: 'https://images.unsplash.com/photo-1507089947368-19c1da9775ae?auto=format&fit=crop&w=1600&q=80'
    },
    'pack-reforma-integral-38700': {
        isProductPack: true,
        afterImage: '/img/packs/pack-integral-1600.webp',
        beforeImage: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1600&q=80'
    },
    'revalorizacion-chalet-sant-cugat': {
        isProductPack: false,
        afterImage: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80',
        beforeImage: 'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?auto=format&fit=crop&w=1600&q=80'
    }
};

const ProjectDetail = () => {
    const { slug } = useParams();
    const { t, currentLang } = useLanguage();

    const labels = t('projectDetail.labels');
    const projectsData = t('projectDetail.projects');

    const meta = projectMeta[slug] || projectMeta['pack-reforma-bano-6900'];
    const project = {
        ...meta,
        ...(projectsData[slug] || projectsData['pack-reforma-bano-6900'])
    };

    const getLangPath = (path) => {
        return currentLang && currentLang !== 'es' ? `/${currentLang}${path}` : path;
    };

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        Name: '',
        Phone: '',
        Email: '',
        Location: '',
        FinancingPlan: labels.financing_6m_value,
        Message: project.formMessage || (project.isProductPack ? labels.form_message_default : labels.form_message_project)
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const fields = {
                name: formData.Name,
                phone: formData.Phone,
                email: formData.Email,
                location: formData.Location,
                message: formData.Message,
                url: window.location.pathname
            };
            if (project.isProductPack) {
                fields.pack = project.titleBold;
                fields.timeline = formData.FinancingPlan;
            }
            await submitLead({
                subject: t('contact.form_subject.studio'),
                fields
            });
        } catch (err) {
            console.error('[Project] No se pudo enviar la solicitud:', err);
        } finally {
            setSubmitting(false);
            setFormSubmitted(true);
        }
    };

    const getWhatsAppMessage = () => {
        const lines = [
            project.formMessage || (project.isProductPack ? labels.form_message_default : labels.form_message_project),
            `${t('contact.form_labels.name')}: ${formData.Name}`,
            `${t('contact.form_labels.phone')}: ${formData.Phone}`,
            `${t('contact.form_labels.location')}: ${formData.Location}`
        ];
        if (project.isProductPack) lines.push(`${t('contact.form_labels.timeline')}: ${formData.FinancingPlan}`);
        if (formData.Message) lines.push(`${t('contact.form_labels.message')}: ${formData.Message}`);
        return lines.join('\n');
    };

    return (
        <div className="inner-page-content" data-page="lab">
            <SEOMetaTags title={`3claus Studio | ${project.titleBold}`} />

            {/* Section - Inner Page Headline (Idéntico a project-details.html) */}
            <div className="mxd-section mxd-section-inner-headline padding-text-pre-block">
                <div className="mxd-container grid-container">
                    <div className="mxd-block loading-wrap">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                {/* Botón Volver */}
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-project-link loading__fade mb-4 mb-xl-0">
                                        <Link to={getLangPath('/studio')} className="btn btn-anim btn-line-small btn-muted slide-right-up">
                                            <span className="btn-caption">{labels.back_to_studio}</span>
                                            <i className="ph ph-arrow-left"></i>
                                        </Link>
                                    </div>
                                </div>

                                {/* Título Principal */}
                                <div className="col-12 col-xl-10 mxd-grid-item no-margin">
                                    <div className="mxd-block__content">
                                        <div className="mxd-block__inner-headline loading__item">
                                            <h1 className="inner-headline__title">
                                                <span>{project.titleBold}</span> {project.titleRest}
                                            </h1>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-0 mt-5">
                                <div className="col-12 col-xl-2"></div>

                                {/* Párrafo e Información Técnica Nativa Rayo (mxd-data-list) */}
                                <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                    <div className="inner-headline__paragraph loading__item mb-4">
                                        <p className="t-large t-bright">{project.subtitle}</p>
                                    </div>

                                    <div className="inner-headline__data">
                                        <div className="mxd-data-list">
                                            <div className="container-fluid p-0">
                                                <div className="row g-0">
                                                    <div className="col-12 col-md-6 col-xl-6 mxd-data-list__column loading__item">
                                                        {project.isProductPack ? (
                                                            <>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.price_closed}</p>
                                                                    <p className="mxd-data-list__content">{project.price}</p>
                                                                </div>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.down_payment}</p>
                                                                    <p className="mxd-data-list__content">{project.downPayment}</p>
                                                                </div>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.financing_remaining}</p>
                                                                    <p className="mxd-data-list__content">{project.financingOptions}</p>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.location}</p>
                                                                    <p className="mxd-data-list__content">{project.location}</p>
                                                                </div>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.service}</p>
                                                                    <p className="mxd-data-list__content">{project.category}</p>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="col-12 col-md-6 col-xl-6 mxd-data-list__column loading__item">
                                                        {project.isProductPack ? (
                                                            <>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.surface_included}</p>
                                                                    <p className="mxd-data-list__content">{project.location}</p>
                                                                </div>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{project.gift1Name || labels.gift_1}</p>
                                                                    <p className="mxd-data-list__content text-success fw-bold">{project.gift1Value || labels.gift_1_default}</p>
                                                                </div>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{project.gift2Name || labels.gift_2}</p>
                                                                    <p className="mxd-data-list__content text-success fw-bold">{project.gift2Value || labels.gift_2_default}</p>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.duration_guarantee}</p>
                                                                    <p className="mxd-data-list__content">{project.duration} ({project.guarantee})</p>
                                                                </div>
                                                                <div className="mxd-data-list__item">
                                                                    <p className="mxd-data-list__name">{labels.year}</p>
                                                                    <p className="mxd-data-list__content">{project.date}</p>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Botón CTA debajo de la ficha técnica */}
                                    {project.isProductPack && (
                                        <div className="mt-4 pt-2">
                                            <a href="#solicitar-pack-form" className="btn btn-anim btn-primary btn-large slide-right-up">
                                                <span className="btn-caption">{labels.request_pack} {project.packPriceShort || "6.900 €"}</span>
                                                <i className="ph-bold ph-arrow-down-right"></i>
                                            </a>
                                        </div>
                                    )}
                                </div>

                                {/* Etiquetas de Cabecera */}
                                <div className="col-12 col-xl-4 mxd-grid-item no-margin mt-4 mt-xl-0">
                                    <div className="inner-headline__tags loading__fade">
                                        {project.tags.map((tag, idx) => (
                                            <span key={idx} className="tag tag-default tag-outline-medium me-2 mb-2">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Divider Hero Image (mxd-divider) */}
            <div className="mxd-section">
                <div className="mxd-container">
                    <div className="mxd-divider loading__fade">
                        <img
                            src={project.afterImage}
                            alt={project.titleBold}
                            className="img-fluid w-100"
                            style={{ maxHeight: '680px', objectFit: 'cover', borderRadius: '24px' }}
                        />
                    </div>
                </div>
            </div>

            {/* Section - Project Details (Idéntico a project-details.html) */}
            <div className="mxd-section mxd-project overflow-hidden padding-pre-grid">
                <div className="mxd-container grid-container">
                    {/* Challenge Block */}
                    <div className="mxd-project__block pre-grid">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-5 mxd-grid-item no-margin">
                                    <div className="mxd-project__subtitle">
                                        <h2 className="reveal-type anim-uni-in-up">
                                            {project.challengeTitle || (project.isProductPack ? labels.challenge_title_pack : labels.challenge_title_project)}
                                        </h2>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                    <div className="mxd-project__content">
                                        <div className="mxd-project__paragraph">
                                            <p className="t-large t-bright anim-uni-in-up">
                                                {project.challenge}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Images Cards Block (Antes / Después) */}
                    <div className="mxd-project__block no-margin my-5">
                        <div className="mxd-project-cards">
                            <div className="container-fluid px-0">
                                <div className="row gx-4 gy-4">
                                    <div className="col-12 col-xl-6 mxd-project-cards__item mxd-grid-item">
                                        <div className="mxd-project-cards__inner bg-base-tint radius-m p-3">
                                            <div className="mb-3">
                                                <span className="tag tag-default tag-permanent">{project.beforeTag || labels.before_tag}</span>
                                            </div>
                                            <img src={project.beforeImage} alt={labels.before_image_alt} className="img-fluid w-100 radius-m" style={{ height: '420px', objectFit: 'cover' }} />
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-project-cards__item mxd-grid-item">
                                        <div className="mxd-project-cards__inner bg-accent radius-m p-3">
                                            <div className="mb-3">
                                                <span className="tag tag-default tag-permanent">
                                                    {project.afterTag || (project.isProductPack ? `${labels.result_pack_tag} ${project.packPriceShort || "6.900 €"}` : labels.result_project_tag)}
                                                </span>
                                            </div>
                                            <img src={project.afterImage} alt={labels.result_image_alt} className="img-fluid w-100 radius-m" style={{ height: '420px', objectFit: 'cover' }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Solution Block con Listado Escaneable y de Alto Contraste */}
                    <div className="mxd-project__block">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-5 mxd-grid-item no-margin">
                                    <div className="mxd-project__subtitle">
                                        <h2 className="reveal-type anim-uni-in-up">
                                            {project.solutionTitle || (project.isProductPack ? labels.solution_title_pack : labels.solution_title_project)}
                                        </h2>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-7 mxd-grid-item no-margin">
                                    <div className="mxd-project__content">
                                        <div className="mxd-project__paragraph medium-text">
                                            <p className="t-large t-bright mb-4">
                                                {project.solution}
                                            </p>

                                            {/* Reutilización Novedosa del Estilo mxd-data-list de Rayo */}
                                            {project.detailsItems ? (
                                                <div className="mxd-data-list mt-4">
                                                    <div className="container-fluid p-0">
                                                        <div className="row g-0">
                                                            <div className="col-12 col-md-6 col-xl-6 mxd-data-list__column loading__item">
                                                                {project.detailsItems.slice(0, Math.ceil(project.detailsItems.length / 2)).map((item, idx) => (
                                                                    <div key={idx} className="mxd-data-list__item">
                                                                        <p className="mxd-data-list__name">{item.name}</p>
                                                                        <p className="mxd-data-list__content">{item.content}</p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="col-12 col-md-6 col-xl-6 mxd-data-list__column loading__item">
                                                                {project.detailsItems.slice(Math.ceil(project.detailsItems.length / 2)).map((item, idx) => (
                                                                    <div key={idx} className="mxd-data-list__item">
                                                                        <p className="mxd-data-list__name">{item.name}</p>
                                                                        <p className="mxd-data-list__content">{item.content}</p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : project.solutionItems ? (
                                                <ul className="mxd-check-list mt-4">
                                                    {project.solutionItems.map((item, idx) => (
                                                        <li key={idx} className="anim-uni-in-up">
                                                            <i className="ph ph-check"></i>
                                                            <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Formulario de Solicitud Directa del Pack */}
            <div id="solicitar-pack-form" className="mxd-section mxd-section-inner-form padding-default my-5">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline">
                                        <p className="mxd-point-subtitle">
                                            <i className={`${project.isProductPack ? "ph-bold ph-shower" : "ph-bold ph-chats-circle"} me-2`}></i>
                                            <span>{project.formBadge || (project.isProductPack ? labels.form_badge_pack : labels.form_badge_project)}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-8 mxd-grid-item no-margin">
                                    <div className="mxd-block__content contact">
                                        <div className="mxd-block__inner-form loading__fade">
                                            <div className="form-container">
                                                {/* Acciones de Contacto: Móvil (Llamar + WhatsApp) */}
                                                <div className="d-flex d-md-none flex-column gap-3 mb-5">
                                                    <a
                                                        href={`tel:${PHONE_NUMBER}`}
                                                        className="btn btn-anim btn-primary btn-large slide-right-up w-100"
                                                    >
                                                        <span className="btn-caption">{labels.call_now}</span>
                                                        <i className="ph-bold ph-phone-call"></i>
                                                    </a>
                                                    <a
                                                        href={whatsappLink(getWhatsAppMessage())}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-anim btn-outline btn-large slide-right-up w-100"
                                                        style={{ borderColor: '#25d366', color: '#25d366' }}
                                                    >
                                                        <span className="btn-caption">{t('lab.contact_box.whatsapp_btn')}</span>
                                                        <i className="ph-bold ph-whatsapp-logo"></i>
                                                    </a>
                                                </div>

                                                <h3 className="h2 mb-2">{project.formTitle || (project.isProductPack ? labels.form_title_default : labels.form_title_project)}</h3>
                                                <p className="text-secondary t-medium mb-4">
                                                    {project.formDescription || (project.isProductPack ? labels.form_description_pack : labels.form_description_project)}
                                                </p>

                                                {formSubmitted ? (
                                                    <div className="text-center py-4">
                                                        <i className="ph-fill ph-smiley-wink fs-1 mb-3 text-tertiary d-block"></i>
                                                        <p className="h3 mb-2">{project.replyTitle || labels.reply_title}</p>
                                                        <span className="t-large text-secondary">
                                                            {project.replyText || (project.isProductPack ? labels.reply_text_default : labels.reply_text_project)}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <form className="form contact-form" id="pack-form" onSubmit={handleSubmit}>
                                                        <div className="container-fluid p-0">
                                                            <div className="row gx-0">
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="text"
                                                                        name="Name"
                                                                        placeholder={labels.name_placeholder}
                                                                        required
                                                                        value={formData.Name}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="tel"
                                                                        name="Phone"
                                                                        placeholder={labels.phone_placeholder}
                                                                        required
                                                                        value={formData.Phone}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="text"
                                                                        name="Location"
                                                                        placeholder={labels.location_placeholder}
                                                                        required
                                                                        value={formData.Location}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                {project.isProductPack && (
                                                                    <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                        <select
                                                                            name="FinancingPlan"
                                                                            value={formData.FinancingPlan}
                                                                            onChange={handleChange}
                                                                        >
                                                                            {(project.financingOptionsList || labels.default_financing_options).map((option) => (
                                                                                <option key={option.value} value={option.value}>{option.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                )}

                                                                <div className="col-12 mxd-grid-item anim-uni-in-up">
                                                                    <textarea
                                                                        name="Message"
                                                                        placeholder={project.messagePlaceholder || (project.isProductPack ? labels.message_placeholder_pack : labels.message_placeholder_project)}
                                                                        value={formData.Message}
                                                                        onChange={handleChange}
                                                                    ></textarea>
                                                                </div>

                                                                <div className="col-12 mxd-grid-item anim-uni-in-up mt-4">
                                                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                                        <button
                                                                            className="btn btn-anim btn-default btn-large btn-opposite slide-right-up"
                                                                            type="submit"
                                                                            disabled={submitting}
                                                                        >
                                                                            <span className="btn-caption">{project.submitLabel || (project.isProductPack ? labels.submit_pack : labels.submit_project)}</span>
                                                                            <i className="ph-bold ph-arrow-up-right"></i>
                                                                        </button>

                                                                        {/* Teléfono (Escritorio - Alineado a la Derecha) */}
                                                                        <div className="d-none d-md-flex align-items-center ms-auto">
                                                                            <span className="t-large t-bright">
                                                                                <i className="ph-bold ph-phone-call me-2"></i>
                                                                                {PHONE_DISPLAY}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </form>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Final */}
            <CTASection
                title={project.ctaTitle || (project.isProductPack ? labels.cta_title_default : labels.cta_title_project)}
                description={project.ctaDescription || (project.isProductPack ? labels.cta_description_default : labels.cta_description_project)}
                buttonText={project.ctaButton || labels.cta_button_default}
                buttonLink={whatsappLink(project.formMessage || (project.isProductPack ? labels.form_message_default : labels.form_message_project))}
                icon="/img/icons/300x300_obj-cta-01.webp"
                iconType="image"
            />
        </div>
    );
};

export default ProjectDetail;
